// Simple hash-based router for static deployment
// The House of Chopras - Navigation System

/**
 * Route configuration for the router
 */
export interface Route {
  /** The path segment (without #), e.g., 'house', 'author', '' for root */
  path: string;
  /** Function to render the page content */
  render: () => void | Promise<void>;
  /** Optional cleanup function called when navigating away */
  cleanup?: () => void;
  /** Whether this is a hidden/easter egg route (not shown in navigation) */
  hidden?: boolean;
}

/**
 * Navigation event detail passed to route change listeners
 */
export interface NavigationEvent {
  /** Previous route path */
  from: string;
  /** New route path */
  to: string;
  /** Whether navigation was prevented */
  prevented: boolean;
}

/**
 * Callback type for route change listeners
 */
export type RouteChangeListener = (event: NavigationEvent) => void;

/**
 * Transition handler type for page transitions
 */
export type TransitionHandler = (
  from: string,
  to: string,
  renderNewPage: () => Promise<void>
) => Promise<void>;

/**
 * Hash-based router for single-page application navigation
 *
 * Supports:
 * - Hash-based routing (e.g., #house, #author)
 * - Page cleanup on navigation
 * - Before/after navigation hooks
 * - Route guards for conditional navigation
 * - Hidden routes for easter eggs
 * - Page transitions with custom handlers
 */
class Router {
  private routes: Route[] = [];
  private currentPath = '';
  private currentRoute: Route | null = null;
  private beforeNavigateCallbacks: ((from: string, to: string) => boolean | void)[] = [];
  private afterNavigateCallbacks: RouteChangeListener[] = [];
  private isNavigating = false;
  private transitionHandler: TransitionHandler | null = null;

  constructor() {
    // Listen for hash changes
    window.addEventListener('hashchange', () => this.handleHashChange());
    // Handle initial load
    window.addEventListener('load', () => this.handleHashChange());
  }

  /**
   * Register routes with the router
   * @param routes Array of route configurations
   */
  register(routes: Route[]): void {
    this.routes = routes;
  }

  /**
   * Set the transition handler for page transitions
   * @param handler Function that handles transition animations
   */
  setTransitionHandler(handler: TransitionHandler): void {
    this.transitionHandler = handler;
  }

  /**
   * Clear the transition handler
   */
  clearTransitionHandler(): void {
    this.transitionHandler = null;
  }

  /**
   * Add a single route
   * @param route Route configuration to add
   */
  addRoute(route: Route): void {
    // Check for duplicate paths
    const existingIndex = this.routes.findIndex(r => r.path === route.path);
    if (existingIndex >= 0) {
      this.routes[existingIndex] = route;
    } else {
      this.routes.push(route);
    }
  }

  /**
   * Navigate to a specific path
   * @param path The path to navigate to (without #)
   */
  navigate(path?: string): void {
    if (path !== undefined) {
      // Check if navigation is allowed
      if (!this.canNavigate(this.currentPath, path)) {
        return;
      }
      window.location.hash = path;
      return;
    }

    // If no path provided, trigger navigation based on current hash
    this.handleHashChange();
  }

  /**
   * Handle hash change events
   */
  private async handleHashChange(): Promise<void> {
    if (this.isNavigating) return;
    this.isNavigating = true;

    try {
      const hash = window.location.hash.slice(1) || '';
      const previousPath = this.currentPath;

      // Check if navigation should proceed
      if (hash !== previousPath && !this.canNavigate(previousPath, hash)) {
        // Restore previous hash
        window.location.hash = previousPath;
        this.isNavigating = false;
        return;
      }

      // Cleanup current route before navigating
      if (this.currentRoute?.cleanup) {
        try {
          this.currentRoute.cleanup();
        } catch (error) {
          console.error('Error during route cleanup:', error);
        }
      }

      this.currentPath = hash;
      const route = this.routes.find(r => r.path === hash);
      const targetRoute = route ?? this.routes.find(r => r.path === '');

      if (targetRoute) {
        this.currentRoute = targetRoute;

        // Use transition handler if available and not initial load
        if (this.transitionHandler && previousPath !== hash) {
          await this.transitionHandler(previousPath, hash, async () => {
            await this.renderRoute(targetRoute);
          });
        } else {
          await this.renderRoute(targetRoute);
        }

        // Reset scroll position after transition/render completes
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }

      // Notify after-navigation listeners
      this.notifyAfterNavigate({
        from: previousPath,
        to: hash,
        prevented: false,
      });
    } finally {
      this.isNavigating = false;
    }
  }

  /**
   * Render a route and manage focus for accessibility
   */
  private async renderRoute(route: Route): Promise<void> {
    try {
      await route.render();

      // Manage focus after navigation for keyboard users
      this.manageFocusAfterNavigation();
    } catch (error) {
      console.error('Error rendering route:', error);
      this.renderErrorPage();
    }
  }

  /**
   * Manage focus after page navigation for accessibility
   * Ensures keyboard users have a logical focus point after navigation
   */
  private manageFocusAfterNavigation(): void {
    // Small delay to ensure DOM is updated
    requestAnimationFrame(() => {
      const app = document.getElementById('app');
      if (!app) return;

      // Look for a main heading or primary focusable element
      const focusTargets = [
        // Skip link target
        app.querySelector('[data-focus-target]'),
        // Main content area
        app.querySelector('main'),
        app.querySelector('[role="main"]'),
        // Page heading
        app.querySelector('h1'),
        // First focusable element in navigation
        app.querySelector('nav button, nav a'),
        // First focusable element
        app.querySelector('button, a, [tabindex="0"]'),
      ];

      for (const target of focusTargets) {
        if (target instanceof HTMLElement) {
          // Make the element focusable if it isn't already
          if (!target.hasAttribute('tabindex') && !['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)) {
            target.setAttribute('tabindex', '-1');
          }
          target.focus({ preventScroll: false });
          break;
        }
      }
    });
  }

  /**
   * Render an error page when route rendering fails
   */
  private renderErrorPage(): void {
    const app = document.getElementById('app');
    if (!app) return;

    app.innerHTML = `
      <div class="page container">
        <h2 class="terminal-prompt">> ERROR</h2>
        <p>Signal lost. Connection terminated.</p>
        <button class="nav-link" onclick="window.location.hash=''">
          > RECONNECT
        </button>
      </div>
    `;
  }

  /**
   * Check if navigation is allowed using before-navigate guards
   */
  private canNavigate(from: string, to: string): boolean {
    for (const callback of this.beforeNavigateCallbacks) {
      const result = callback(from, to);
      if (result === false) {
        return false;
      }
    }
    return true;
  }

  /**
   * Notify after-navigate listeners
   */
  private notifyAfterNavigate(event: NavigationEvent): void {
    for (const callback of this.afterNavigateCallbacks) {
      try {
        callback(event);
      } catch (error) {
        console.error('Error in after-navigate callback:', error);
      }
    }
  }

  /**
   * Get the current path
   */
  getCurrentPath(): string {
    return this.currentPath;
  }

  /**
   * Get the current route
   */
  getCurrentRoute(): Route | null {
    return this.currentRoute;
  }

  /**
   * Get a route by path
   */
  getRoute(path: string): Route | undefined {
    return this.routes.find(r => r.path === path);
  }

  /**
   * Get all registered routes
   * @param includeHidden Whether to include hidden routes
   */
  getRoutes(includeHidden = false): Route[] {
    if (includeHidden) {
      return [...this.routes];
    }
    return this.routes.filter(r => !r.hidden);
  }

  /**
   * Add a callback to be called before navigation
   * Return false from the callback to prevent navigation
   * @param callback Function to call before navigation
   * @returns Function to remove the callback
   */
  beforeNavigate(callback: (from: string, to: string) => boolean | void): () => void {
    this.beforeNavigateCallbacks.push(callback);
    return () => {
      const index = this.beforeNavigateCallbacks.indexOf(callback);
      if (index >= 0) {
        this.beforeNavigateCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Add a callback to be called after navigation
   * @param callback Function to call after navigation
   * @returns Function to remove the callback
   */
  afterNavigate(callback: RouteChangeListener): () => void {
    this.afterNavigateCallbacks.push(callback);
    return () => {
      const index = this.afterNavigateCallbacks.indexOf(callback);
      if (index >= 0) {
        this.afterNavigateCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Check if a route exists
   */
  hasRoute(path: string): boolean {
    return this.routes.some(r => r.path === path);
  }

  /**
   * Replace the current URL without triggering navigation
   * Useful for updating the URL without a full page transition
   */
  replaceState(path: string): void {
    window.history.replaceState(null, '', `#${path}`);
    this.currentPath = path;
  }
}

// Singleton router instance
export const router = new Router();

/**
 * Convenience function to navigate to a path
 * @param path The path to navigate to (without #)
 */
export function navigateTo(path: string): void {
  router.navigate(path);
}

/**
 * Create a link element with proper navigation handling
 * @param path The path to link to
 * @param text The link text
 * @param className Optional CSS class
 */
export function createLink(path: string, text: string, className = 'nav-link'): HTMLAnchorElement {
  const link = document.createElement('a');
  link.href = `#${path}`;
  link.textContent = text;
  link.className = className;
  return link;
}

/**
 * Create a navigation button with proper click handling
 * @param path The path to navigate to
 * @param text The button text
 * @param className Optional CSS class
 */
export function createNavButton(path: string, text: string, className = 'nav-link'): HTMLButtonElement {
  const button = document.createElement('button');
  button.textContent = text;
  button.className = className;
  button.addEventListener('click', () => navigateTo(path));
  return button;
}
